'use client';

import { Image } from '@bw/elements';
import type { InputEvent } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FORM_ERROR } from '../../constants';
import { useAppContext } from '../form-stepper/StepperProvider';
import { FormButton } from '../utility/formButton/formButton';
import { ThemeColor } from '../../theme';
import styles from './ImageColTextForm.module.scss';
import type { ImageWithTextForm } from '@bw/apis/contentful';

type Inputs = {
	leadInsuranceTypeId: string;
	postalCode: number;
};

type ImageColTextFormProps = ImageWithTextForm['fields'];

export function ImageColTextFormClient(props: ImageColTextFormProps) {
	const buttonData = props?.button;
	const imageData = props?.image?.fields.image?.fields;
	const optionItems = props?.optionsItems;
	const isImageSmall = props.isImageSmall;
	const { showFormStepper, setShowFormStepper } = useAppContext();

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm<Inputs>();
	const onSubmit: SubmitHandler<Inputs> = (data) => {
		localStorage.setItem('leadInsuranceTypeId', data.leadInsuranceTypeId);
		localStorage.setItem('postalCode', data.postalCode.toString());
		setShowFormStepper(!showFormStepper);
		reset();
	};

	const handleOnInput = (e: InputEvent<HTMLInputElement>) => {
		e.currentTarget.value = e.currentTarget.value.replace(/[^0-9]/g, '');
	};

	return (
		<section>
			<div className="container container--max">
				<div
					data-aos="fade-up"
					className={`${styles['imageColTextForm']}  ${
						isImageSmall ? styles['imageColTextForm--imageSmallDesign'] : ''
					}`}
				>
					<article className={styles.textSection}>
						{props?.subHeading && (
							<span className="headingTagline" data-aos="fade-up">
								{props.subHeading}
							</span>
						)}
						<h2 data-aos="fade-up">{props?.heading}</h2>
						<p className={styles.headingDescriptionText} data-aos="fade-up">
							{props?.description}
						</p>
						<form
							id={props?.formId}
							data-aos="fade-up"
							onSubmit={handleSubmit(onSubmit)}
						>
							<input type="hidden" name="gclid" />
							<input type="hidden" name="ga_id" />

							{props?.formHeading && (
								<h3 className={styles.formHeading}>{props?.formHeading}</h3>
							)}
							<label htmlFor="start-a-quote-dropdown">
								{props?.dropdownHeading
									? props.dropdownHeading
									: "I'm Interested in"}
							</label>
							<select
								id="start-a-quote-dropdown"
								{...register('leadInsuranceTypeId', {
									required: FORM_ERROR.REQUIRED,
								})}
								defaultValue=""
							>
								<option value="">Select Insurance</option>
								{optionItems &&
									optionItems.map((item, index) => (
										<option key={index} value={index + 1}>
											{item}
										</option>
									))}
							</select>

							<span>
								{errors['leadInsuranceTypeId']
									? errors['leadInsuranceTypeId'].message
									: ''}
							</span>
							<label htmlFor="postalCode">Zip Code</label>
							<input
								type="text"
								id="postalCode"
								inputMode="numeric"
								onInput={handleOnInput}
								minLength={5}
								maxLength={5}
								{...register('postalCode', {
									required: FORM_ERROR.REQUIRED,
									pattern: {
										value: /^(?!00000)\d{5}$/,
										message: 'Enter a valid postal code',
									},
								})}
							/>

							<span>
								{errors['postalCode'] ? errors['postalCode'].message : ''}
							</span>

							<div className={styles.buttonContainer}>
								{buttonData && (
									<FormButton
										id="stepper-form"
										title={buttonData.fields.title ?? ''}
										textColor={buttonData.fields.textColour as ThemeColor}
										backgroundColor={buttonData.fields.backgroundColour as ThemeColor}
										borderColor={buttonData.fields.borderColour as ThemeColor}
										type="submit"
									/>
								)}
							</div>
						</form>
					</article>
					<figure className={styles.imageSection} data-aos="fade-up">
						{imageData?.file?.url && (
							<Image
								alt={imageData?.title ?? ''}
								src={imageData.file?.url}
								width={imageData.file?.details.image?.width}
								height={imageData.file?.details.image?.height}
								sizes={`${'(max-width: 1149px) 100vw,'} ${isImageSmall ? '42' : '58'}vw`}
							/>
						)}
					</figure>
				</div>
			</div>
		</section>
	);
}
